// Racket Lab Admin — i18n wrapper (QUICK 1.B runtime).
//
// Bridges the static EN/ES dictionaries (racketLab.en.js / racketLab.es.js)
// with the TEAM OS LangProvider so admin-shell components can render in the
// user's active language without inflating the global TRANSLATIONS table.
//
// Pattern of use:
//
//   import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";
//   const t = useRacketLabAdminT();
//   <span>{t("nav.items.dashboard")}</span>
//
// Fallback chain:  active_lang → es → en  (English is final source-of-truth).
//
// HARD RULES:
//   1. Never resolves anything outside the racket_lab namespace tree.
//   2. Vars interpolation matches global `t()` semantics: "{name}" → value.
//   3. Missing keys return the path literal so the gap is visible during dev.
//   4. No JSX. No side effects. Pure hook + helper.

import { useCallback } from "react";
import { useLang } from "./index";
import racketLabEn from "./racketLab.en";
import racketLabEs from "./racketLab.es";

const DICTS = { en: racketLabEn, es: racketLabEs };

const _resolve = (dict, parts) => {
  let node = dict;
  for (const p of parts) {
    if (node && typeof node === "object" && p in node) node = node[p];
    else return undefined;
  }
  return typeof node === "string" ? node : undefined;
};

const _interpolate = (str, vars) => {
  if (!vars) return str;
  return Object.keys(vars).reduce(
    (acc, k) => acc.replaceAll(`{${k}}`, vars[k]),
    str,
  );
};

/**
 * useRacketLabAdminT — returns `t(path, vars?)` scoped to the racket_lab
 * admin dictionary. Honors the active LangProvider language with a
 * deterministic `active → es → en` fallback chain.
 *
 * Accepts both bare paths (`"nav.items.dashboard"`) and the
 * `racketLab.`-prefixed form used by mapping files
 * (`"racketLab.nav.items.dashboard"`). The prefix is stripped before
 * resolution so the dictionary tree stays flat.
 *
 * The returned function is stable per language change (useCallback).
 */
export const useRacketLabAdminT = () => {
  const { lang } = useLang();
  return useCallback((path, vars) => {
    if (!path || typeof path !== "string") return "";
    // Strip the optional `racketLab.` prefix used by enum→label maps.
    const stripped = path.startsWith("racketLab.") ? path.slice("racketLab.".length) : path;
    const parts = stripped.split(".");

    // Active language (with normalization for pt-br style codes).
    const active = String(lang || "en").replace(/-/g, "_");

    // Deterministic fallback chain: active → es → en. We dedupe so the
    // same dict isn't walked twice for ES/EN users.
    const chain = [];
    for (const code of [active, "es", "en"]) {
      if (DICTS[code] && !chain.includes(code)) chain.push(code);
    }

    for (const code of chain) {
      const str = _resolve(DICTS[code], parts);
      if (str !== undefined) return _interpolate(str, vars);
    }

    // Key missing — return the literal path so the surface is visible.
    return path;
  }, [lang]);
};

export default useRacketLabAdminT;
